import React, { useEffect, useRef } from "react";

import { usePyForm } from "@performyard/py-components";

interface HasChangesEffectProps {
	onHasChanges: () => void;
}

const HasChangesEffect: React.FC<HasChangesEffectProps> = ({ onHasChanges }) => {
	// Ref-stabilized: fire once on mount, never re-arm after the parent flips back to safe (PY-12605).
	const onHasChangesRef = useRef(onHasChanges);
	onHasChangesRef.current = onHasChanges;
	useEffect(() => {
		onHasChangesRef.current();
	}, []);
	return null;
};

export interface HasChangesBridgeProps {
	onHasChanges: () => void;
}

/**
 * Subscribes to a PYForm's `isDirty` state and fires `onHasChanges` exactly once when
 * the form first becomes dirty. Used to arm the unsaved-changes guard. Latch via
 * `armedRef` because TanStack Form flips `isDirty` back to false when the user resets
 * to defaults — we don't want to disarm in that case.
 *
 * Must be rendered inside a `<PYForm>`.
 */
export const HasChangesBridge: React.FC<HasChangesBridgeProps> = ({ onHasChanges }) => {
	const pyForm = usePyForm();
	const armedRef = useRef(false);
	return (
		<pyForm.Subscribe selector={(s) => s.isDirty}>
			{(hasChanges) => {
				if (hasChanges) armedRef.current = true;
				return armedRef.current ? <HasChangesEffect onHasChanges={onHasChanges} /> : null;
			}}
		</pyForm.Subscribe>
	);
};
HasChangesBridge.displayName = "HasChangesBridge";

export default HasChangesBridge;
